import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import icons from "../../icons.config";
import { IMAGE_URL } from "../../api-urls";
import {
  fetchCartItems,
  addToCart,
  removeFromCart,
  incrementDecrementCart,
  clearCartAPI,
  openCart,
  closeCart,
  toggleCartItemSelectionAPI,
} from "../../Providers/cartProvider";
import Lottie from "lottie-react";
import loadingAnimation from "../../Assets/loadingAnimation.json";
import { setClickedItem } from "../../store/itemsSlice"; // Import setClickedItem

const CartSlider = ({ toggleCartSlider }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated || false);

  const { cartItems, totalCount, subTotal, loading, error, isCartOpen, gst, custom_size_data } =
    useSelector((state) => state.cartItems);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCartItems());
    }
  }, [dispatch, isAuthenticated]);

  const handleRemove = (cartId) => {
    dispatch(removeFromCart({ cartId }));
  };

  const handleClearAll = () => {
    dispatch(clearCartAPI());
  };

  const goToShop = () => {
    navigate("/shop");
  };

  const handleQuantityChange = (cartId, incrementOrDecrementAction) => {
    dispatch(
      incrementDecrementCart({
        cartId,
        incrementOrDecrementAction,
      })
    );
  };

  const goToLogin = () => {
    navigate("/login");
  };

  // Handler for navigating to product view page
  const handleItemClick = (item) => {
    // Construct item object for Redux consistency with Shop component
    const productItem = {
      product_category: item.product_category || [""], // Fallback if missing
      product_name: item.product_name || "",
      default_variant: {
        id: item.variant_id || "0",
        title: item.variant_title || item.product_name || "", // Fallback to product_name
      },
    };
    dispatch(setClickedItem(productItem));
    // Use item.url directly from API, ensuring absolute path
    const absoluteUrl = item.url.startsWith("/") ? item.url : `/${item.url}`;
    navigate(absoluteUrl);
  };

  return (
    <div className="bg-tertiary h-full bg-white shadow-lg z-50 flex flex-col">
      <div className="px-7 pt-5 flex-grow overflow-y-auto">
        {!isAuthenticated ? (
          <div className="flex flex-col items-center justify-center h-[80%] space-y-6">
            <img
              src={icons.emptyCart}
              alt="Login required"
              className="w-24 h-24 opacity-80 transform transition-transform duration-500 hover:rotate-12"
            />
            <h2 className="text-xl font-outfit text-hcolor font-medium text-center">
              Please Log In
            </h2>
            <p className="text-center text-gray-600 font-outfit text-sm max-w-xs">
              Sign in to view your cart and start shopping with ease!
            </p>
            <button
              onClick={goToLogin}
              className="border-2 py-2 px-6 text-hcolor font-outfit font-medium bg-white hover:bg-hcolor hover:text-primary2 transition-all duration-300 rounded-md"
            >
              Log In
            </button>
            <p className="text-xs text-gray-500">
              Don’t have an account?{" "}
              <span
                onClick={() => navigate("/signup")}
                className="text-hcolor cursor-pointer hover:underline"
              >
                Sign Up
              </span>
            </p>
          </div>
        ) : loading ? (
          <div className="flex justify-center items-center h-[400px]">
            <Lottie
              animationData={loadingAnimation}
              loop={true}
              className="w-[200px] h-[200px]"
            />
          </div>
        ) : error ? (
          <p className="text-center text-red-500">Error: {error}</p>
        ) : cartItems.length > 0 ? (
          <>
            <div className="flex items-center mb-4">
              <h2 className="text-lg font-outfit text-hcolor">
                Cart ({totalCount})
              </h2>
              <button
                className="text-sm text-red-500 font-medium underline ml-auto"
                onClick={handleClearAll}
              >
                Clear All
              </button>
            </div>
            <div className="max-h-[450px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
              {cartItems.map((item, index) => {
                const price = item?.price || 0;
                const discountedPrice = item?.discount_price || 0;
                const image = item?.variant_main_image || "";
                const size = item?.size || "Your Custom size";
                const color = item?.color || "";
                const productName = item?.product_name || "";
                const qty = item?.quantity || 1;
                const variantId = item?.variant_id || "0";
                const cartId = item.cart_id;

                return (
                  <div
                    key={`${variantId}-${index}`}
                    className="relative flex flex-col gap-4 mb-6 px-2 pt-12 pb-4 border-2 border-hcolor rounded-lg shadow-md"
                  >
                    <label className="absolute top-2 left-2 cursor-pointer bg-white p-1 rounded-md shadow-sm z-10">
                      <input
                        type="checkbox"
                        className="w-5 h-5 accent-hcolor"
                        checked={item?.isSelected || false}
                        onChange={() => {
                          if (item?.cart_id) {
                            dispatch(toggleCartItemSelectionAPI(item.cart_id));
                          } else {
                            console.error("cart_id is missing for this item:", item);
                          }
                        }}
                      />
                    </label>

                    <button
                      className="absolute top-2 right-2"
                      onClick={() => handleRemove(item?.cart_id)}
                    >
                      <img src={icons.removeLikedItem} alt="remove item" />
                    </button>

                    <div className="flex gap-4">
                      <div className="w-[30%] lg:w-[20%] lg:h-28">
                        <img
                          src={IMAGE_URL + image}
                          alt={productName}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => handleItemClick(item)} // Add click handler
                        />
                      </div>

                      <div className="flex flex-1 flex-col gap-2">
                        <p
                          className="font-outfit font-bold text-lg cursor-pointer hover:underline"
                          onClick={() => handleItemClick(item)} // Add click handler
                        >
                          {productName}
                        </p>
                        <p className="text-sm text-gray-500">Size: {size}</p>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              handleQuantityChange(cartId, "decrement")
                            }
                            className="text-sm text-hcolor px-2 py-1 border rounded hover:bg-hcolor hover:text-white"
                            disabled={qty <= 1}
                          >
                            <FontAwesomeIcon icon={faMinus} />
                          </button>
                          <span className="text-sm">{qty}</span>
                          <button
                            onClick={() =>
                              handleQuantityChange(cartId, "increment")
                            }
                            className="text-sm text-hcolor px-2 py-1 border rounded hover:bg-hcolor hover:text-white"
                          >
                            <FontAwesomeIcon icon={faPlus} />
                          </button>
                        </div>
                      </div>

                      <div className="text-right">
                        <p className="text-lg font-bold text-primary">
                          Rs.{discountedPrice}
                        </p>
                        <p className="text-sm text-gray-500 line-through">
                          Rs.{price}
                        </p>
                        <p className={`text-sm mt-5 ${item?.isAvailable ? "text-hcolor" : "text-hcolor"}`}>
                          {item?.isAvailable ? "In Stock" : "Out of Stock"}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center mt-40 space-y-4">
            <img
              src={icons.emptyCart}
              alt="Empty Cart"
              className="w-32 transform transition-transform duration-500 hover:rotate-180"
            />
            <p className="text-center text-hcolor">Your cart is empty!</p>
            <button
              onClick={goToShop}
              className="border-2 py-2 px-5 text-hcolor font-outfit font-medium hover:bg-hcolor hover:text-primary2 bg-white transition-all duration-500"
            >
              Shop Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartSlider;
// import React, { useEffect, useState } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faTimes, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
// import { useDispatch } from "react-redux";
// import {
//   clearCart,
//   fetchCartItems,
//   removeFromCart,
//   incrementDecrementCart
// } from "../../Providers/cartProvider";
// import { useNavigate } from "react-router-dom";
// import icons from "../../icons.config";
// import { IMAGE_URL } from "../../api-urls";

// const CartSlider = ({ toggleCartSlider }) => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const [cartItems, setCartItems] = useState([]);
//   const [subTotal, setSubTotal] = useState(0);

//   // Fetch cart items initially and whenever cart updates
//   useEffect(() => {
//     fetchCart();
//   }, [cartItems]);

//   const fetchCart = async () => {
//     const data = await fetchCartItems();
//     setCartItems(data.data);

//     const totalAmount = data.data.reduce((sum, item) => {
//       return sum + (Number(item.discount_price) * item.quantity);
//     }, 0);

//     setSubTotal(totalAmount);
//   };

//   // Remove item and refresh cart
//   const handleRemove = async (product_id, size, color) => {
//     try {
//       await dispatch(removeFromCart({ product_id, size, color })).unwrap();
//       fetchCart();
//     } catch (error) {
//       console.error("❌ Error updating cart:", error);
//     }
//   };

//   // Clear all items and update state
//   const handleClearAll = () => {
//     dispatch(clearCart());
//     setCartItems([]);
//   };

//   // Update quantity and refresh cart
//   const handleQuantityChange = async (variantId, size, color, action) => {
//     await dispatch(incrementDecrementCart({ variantId, size, color, incrementOrDecrementAction: action }));
//     fetchCart();
//   };

//   // Handle checkout
//   const handleCheckout = () => {
//     const gstPercentage = 18;
//     const gstAmount = (subTotal * gstPercentage) / 100;
//     const totalPrice = subTotal + gstAmount;

//     navigate("/checkout", { state: { cart: cartItems, gst: gstAmount, totalPrice } });
//   };

//   return (
//     <div className="fixed top-0 bg-tertiary right-0 w-[90%] lg:w-1/3 h-full bg-white shadow-lg z-50 transition-transform duration-300 ease-in-out flex flex-col">
//       {/* Close Button */}
//       <div className="p-4 flex justify-end">
//         <FontAwesomeIcon icon={faTimes} className="cursor-pointer text-primary1 text-xl" onClick={toggleCartSlider} />
//       </div>

//       <div className="px-7 flex-grow overflow-y-auto">
//         {cartItems.length > 0 ? (
//           <>
//             {/* Cart Heading and Clear All */}
//             <div className="flex items-center mb-4">
//               <h2 className="text-lg font-outfit text-hcolor">Cart ({cartItems.length})</h2>
//               <button className="text-sm text-red-500 font-medium underline ml-auto" onClick={handleClearAll}>
//                 Clear All
//               </button>
//             </div>

//             {/* Cart Items List */}
//             {cartItems.map((item) => (
//               <div key={item.id} className="flex gap-4 mb-6 items-start">
//                 <div className="relative w-[30%] lg:w-[20%]">
//                   <img src={IMAGE_URL + item.variant_main_image} alt={item.product_name} className="w-full object-cover" />
//                   <button className="absolute -top-2 -right-2" onClick={() => handleRemove(item.variant_id, item.size, item.color)}>
//                     <img src={icons.removeLikedItem} alt="remove item" />
//                   </button>
//                 </div>

//                 {/* Details */}
//                 <div className="flex flex-col flex-1 gap-2">
//                   <p className="font-outfit font-bold text-lg">{item.product_name}</p>
//                   <p className="text-sm text-gray-500">Size: {item.size}</p>

//                   {/* Quantity Controls */}
//                   <div className="flex items-center gap-2">
//                     <button onClick={() => handleQuantityChange(item.variant_id, item.size, item.color, "decrement")}
//                       className="text-sm text-hcolor px-2 py-1 border rounded hover:bg-hcolor hover:text-white"
//                       disabled={item.quantity === 1}>
//                       <FontAwesomeIcon icon={faMinus} />
//                     </button>
//                     <span className="text-sm">{item.quantity}</span>
//                     <button onClick={() => handleQuantityChange(item.variant_id, item.size, item.color, "increment")}
//                       className="text-sm text-hcolor px-2 py-1 border rounded hover:bg-hcolor hover:text-white">
//                       <FontAwesomeIcon icon={faPlus} />
//                     </button>
//                   </div>
//                 </div>

//                 {/* Pricing */}
//                 <div className="text-right">
//                   <p className="text-lg font-bold text-primary">Rs.{item.discount_price}</p>
//                   <p className="text-sm text-gray-500 line-through">Rs.{item.price}</p>
//                 </div>
//               </div>
//             ))}
//           </>
//         ) : (
//           <div className="flex flex-col items-center mt-40 space-y-4">
//             <img src={icons.emptyCart} alt="Empty Cart" className="w-32" />
//             <p className="text-center text-hcolor">Your cart is empty!</p>
//           </div>
//         )}
//       </div>

//       {/* Subtotal Section */}
//       {cartItems.length > 0 && (
//         <div className="px-7 py-4 border-t bg-primary2">
//           <div className="flex justify-between text-lg font-medium"><span>Subtotal</span><span>Rs.{subTotal}</span></div>
//           <button className="mt-10 w-full py-3 bg-hcolor text-tertiary" onClick={handleCheckout}>Checkout</button>
//         </div>
//       )}
//     </div>
//   );
// };
//  export default CartSlider;

// import React, { useEffect, useState } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faTimes, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
// import { useDispatch, useSelector } from "react-redux";
// import {

//   clearCart,
//   updateCartItem,
// } from "../../store/cartItemsSlice";
// import { useNavigate } from "react-router-dom";
// import icons from "../../icons.config";
// import { IMAGE_URL } from "../../api-urls";
// import { fetchCartItems,removeFromCart ,incrementDecrementCart} from "../../Providers/cartProvider";

// const CartSlider = ({ toggleCartSlider }) => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const [cartItems, setCartItems] = useState([]);
//   const [subTotal, setSubTotal] = useState(0);

//   // const cartItems = useSelector((state) => state.cartItems?.cartItems || []);
//   console.log("my cart itmesssssssssss-----------", cartItems);

//   useEffect(() => {
//     fetchCart();
//   }, []);

//   const fetchCart = async () => {
//     const data = await fetchCartItems();

//     if (!data || !data.data || !Array.isArray(data.data)) {
//         console.error("Invalid cart data:", data);
//         setCartItems([]); // Set an empty array to prevent errors
//         setSubTotal(0);
//         return;
//     }

//     setCartItems(data.data);

//     const totalAmount = data.data.reduce((sum, item) => {
//         if (item.discount_price) {
//             return sum + (Number(item.discount_price) * item.quantity);
//         }
//         return sum;
//     }, 0);

//     setSubTotal(totalAmount);
//     console.log("Total Amount:", totalAmount);
// };

//   // Handle removing a single cart item
//   const handleRemove = async (itemId, size, color) => {
//     await dispatch(removeFromCart({ product_id: itemId, size, color }));
//     fetchCart(); // Refresh cart after removing an item
//   };

//   // Handle clearing all cart items
//   const handleClearAll = () => {
//     dispatch(clearCart());
//   };

//   // Navigate to shop page
//   const goToShop = () => {
//     navigate("/shop");
//   };

//   // Handle quantity increment/decrement
//   const handleQuantityChange = async (variantId, size, color, action) => {
//     await dispatch(incrementDecrementCart({ variantId, size, color, action }));
//     fetchCart(); // Refresh cart after updating quantity
//   };
//   // Calculate subtotal
//   // const subtotal = cartItems.reduce(
//   //   (total, item) => total + item.discountedPrice * item.quantity,
//   //   0
//   // );

//   // const handleCheckout = () => {
//   //   const cartItemsWithGST = cartItems.map(item => {
//   //     const gst = item.discountedPrice * 0.18;
//   //     return { ...item, gst };
//   //   });

//   //   const totalPrice = cartItemsWithGST.reduce((total, item) => total + item.discountedPrice * item.quantity + item.gst, 0);

//   //   navigate("/checkout", {
//   //     state: {
//   //       cartItems: cartItemsWithGST,
//   //       totalPrice,
//   //     },
//   //   });
//   // };

//   const handleCheckout = () => {
//     const gstPercentage = 18; // Assuming 18% GST
//     const subtotal = cartItems.reduce(
//       (acc, item) => acc + item.discountedPrice * item.quantity,
//       0
//     );
//     const gstAmount = (subtotal * gstPercentage) / 100;
//     const totalPrice = subtotal + gstAmount;

//     // const gst = clickedItem.price * 0.18; // Example GST calculation
//     // const totalPrice = clickedItem.discountedPrice + gst;

//     navigate("/checkout", {
//       state: {
//         cart: cartItems, // Pass the whole cart array
//         gst: gstAmount,
//         totalPrice: totalPrice,
//       },
//     });
//   };

//   const [quantity, setQuantity] = useState(1);

//   return (
//     <div className="fixed top-0 bg-tertiary right-0 w-[90%] lg:w-1/3 h-full bg-white shadow-lg z-50 transition-transform duration-300 ease-in-out flex flex-col">
//       {/* Close Button */}
//       <div className="p-4 flex justify-end items-end">
//         <FontAwesomeIcon
//           icon={faTimes}
//           className="cursor-pointer text-primary1 text-xl"
//           onClick={toggleCartSlider}
//         />
//       </div>

//       <div className="px-7 flex-grow overflow-y-auto">
//         {cartItems.length > 0 ? (
//           <>
//             {/* Cart Heading and Clear All Button */}
//             <div className="flex items-center mb-4">
//               <h2 className="text-lg font-outfit text-hcolor">
//                 Cart ({cartItems.length})
//               </h2>
//               <button
//                 className="text-sm text-red-500 font-medium underline ml-auto"
//                 onClick={handleClearAll}
//               >
//                 Clear All
//               </button>
//             </div>

//             {/* Cart Items List */}
//             {cartItems.map((item) => {
//               // console.log("cart item----", item);
//               const price = item?.price || 0;
//               const discountedPrice =
//                 item?.discount_price || 0;
//               const image =
//                 item?.variant_main_image || "";
//                 const size =item?.size || "";
//                 const color = item?.color || "";
//                 const productName = item?.product_name || "";
//                 const qty = item?.quantity || 1;
//                 const variantId = item?.variant_id ||"0";
//                 const inStock= item?.is_available || "false";

//               return (
//                 <div key={item.id} className="flex gap-4 mb-6 items-start ">
//                   <div className="relative w-[30%] lg:w-[20%]">
//                     <img
//                       src={IMAGE_URL + image}
//                       alt={image}
//                       className="w-full object-cover"
//                     />
//                     <button
//                       className="absolute -top-2 -right-2"
//                       onClick={() => handleRemove(variantId,size, color)}
//                     >
//                       <img src={icons.removeLikedItem} alt="remove item" />
//                     </button>
//                   </div>

//                   {/* Details */}
//                   <div className="flex flex-col flex-1 gap-2">
//                     <p className="font-outfit font-bold text-lg">
//                       {productName}
//                     </p>
//                     <p className="text-sm text-gray-500">
//                       Size: {size}
//                     </p>
//                     {/* Quantity Controls */}
//                     <div className="flex items-center gap-2">
//                       {/* Decrease Quantity */}
//                       <button
//                         onClick={() => handleQuantityChange(variantId,size, color, "decrement")}
//                         className="text-sm text-hcolor px-2 py-1 border rounded hover:bg-hcolor hover:text-white"
//                         disabled={item.quantity === 1}
//                       >
//                         <FontAwesomeIcon icon={faMinus} />
//                       </button>

//                       {/* Display Quantity */}
//                       <span className="text-sm">{qty}</span>

//                       {/* Increase Quantity */}
//                       <button
//                         onClick={() => handleQuantityChange(variantId,size, color, "increment")}
//                         className="text-sm text-hcolor px-2 py-1 border rounded hover:bg-hcolor hover:text-white"
//                       >
//                         <FontAwesomeIcon icon={faPlus} />
//                       </button>
//                     </div>
//                   </div>
//                   {/* Pricing and Stock */}
//                   <div className="text-right">
//                     <p className="text-lg font-bold text-primary">
//                       Rs.{discountedPrice}
//                     </p>
//                     <p className="text-sm text-gray-500 line-through">
//                       Rs.{price}
//                     </p>
//                     <p
//                       className={`text-sm ${
//                         inStock ? "text-green-500" : "text-red-500"
//                       }`}
//                     >
//                       {inStock}
//                     </p>
//                   </div>
//                 </div>
//               );
//             })}
//           </>
//         ) : (
//           <div className="flex flex-col items-center mt-40 space-y-4">
//             <img
//               src={icons.emptyCart} // Replace with your image URL
//               alt="Rotating Animation"
//               className="w-32 transform transition-transform duration-500 hover:rotate-180" // Rotate on hover
//             />
//             <p className="text-center text-hcolor">Your cart is empty!</p>
//             <button
//               onClick={goToShop}
//               className="border-2 py-2 px-5 text-hcolor font-outfit font-medium hover:bg-hcolor hover:text-primary2 bg-white transition-all duration-500"
//             >
//               Shop Now
//             </button>
//           </div>
//         )}
//       </div>

//       {/* Subtotal Section */}
//       {cartItems.length > 0 && (
//         <div className="px-7 py-4 border-t bg-primary2">
//           <div className="flex justify-between text-lg font-medium">
//             <span>Subtotal</span>
//             <span>Rs.{subTotal}</span>
//           </div>
//           <p className="text-sm text-gray-500">
//             Tax Included, shipping calculated at checkout.
//           </p>
//           <button
//             className="mt-10 w-full py-3 bg-hcolor text-tertiary font-medium hover:bg-primary-dark transition-all duration-300"
//             onClick={handleCheckout}
//           >
//             Checkout
//           </button>
//         </div>
//       )}
//     </div>
//   );
// };

// export default CartSlider;

// import React, { useEffect, useState } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faTimes, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
// import { useDispatch, useSelector } from "react-redux";
// import {

//   clearCart,
//   updateCartItem,
// } from "../../Providers/cartProvider";
// import { useNavigate } from "react-router-dom";
// import icons from "../../icons.config";
// import { IMAGE_URL } from "../../api-urls";
// import { fetchCartItems,removeFromCart ,incrementDecrementCart} from "../../Providers/cartProvider";

// const CartSlider = ({ toggleCartSlider }) => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const [cartItems, setCartItems] = useState([]);
//   const [subTotal, setSubTotal] = useState(0);

//   // const cartItems = useSelector((state) => state.cartItems?.cartItems || []);
//   console.log("my cart itmesssssssssss-----------", cartItems);

//   useEffect(() => {
//     fetchCart();
//   }, []);

//   const fetchCart = async () => {
//     const data = await fetchCartItems();
//     setCartItems(data.data);

//     const totalAmount = data.data.reduce((sum, item) => {
//       // Check if discount_price exists (skip the first item which has no price)
//       if (item.discount_price) {
//           // Convert discount_price to number and multiply by quantity
//           return sum + (Number(item.discount_price) * item.quantity);
//       }
//       return sum;
//   }, 0);
//     // for(let i = 0; i < data.data.length; i){
//     //  const singleData = data.data[i];
//     //  console.log(singleData);
//     //  const discountedPrice = parseInt(singleData.discount_price);
//     //  const qty = singleData.quantity;

//     //  const price = discountedPrice * qty;
//     //  total+=price;

//     // }

//     setSubTotal(totalAmount);
//     console.log(totalAmount);

//     console.log("cart data from api-----", data);
//   };

//   const handleRemove = async (product_id, size, color) => {
//     try {
//       const updatedCart = await dispatch(
//         removeFromCart({ product_id, size, color })
//       ).unwrap();

//       console.log("🛒 Updated Cart:", updatedCart);
//       setCartItems(updatedCart.data);

//     } catch (error) {
//       console.error("❌ Error updating cart:", error);
//     }
//   };

//   // Handle clearing all cart items
//   const handleClearAll = () => {
//     dispatch(clearCart());
//   };

//   // Navigate to shop page
//   const goToShop = () => {
//     navigate("/shop");
//   };

//   // Handle quantity increment/decrement
//   const handleQuantityChange = (variantId,size, color, incrementOrDecrementAction) => {
//     console.log("Updating Quantity for Item ID:", variantId,size, color, "incrementOrDecrementAction:", incrementOrDecrementAction);
//     dispatch(incrementDecrementCart({ variantId,size, color, incrementOrDecrementAction }));
//   };

//   // Calculate subtotal
//   // const subtotal = cartItems.reduce(
//   //   (total, item) => total + item.discountedPrice * item.quantity,
//   //   0
//   // );

//   // const handleCheckout = () => {
//   //   const cartItemsWithGST = cartItems.map(item => {
//   //     const gst = item.discountedPrice * 0.18;
//   //     return { ...item, gst };
//   //   });

//   //   const totalPrice = cartItemsWithGST.reduce((total, item) => total + item.discountedPrice * item.quantity + item.gst, 0);

//   //   navigate("/checkout", {
//   //     state: {
//   //       cartItems: cartItemsWithGST,
//   //       totalPrice,
//   //     },
//   //   });
//   // };

//   const handleCheckout = () => {
//     const gstPercentage = 18; // Assuming 18% GST
//     const subtotal = cartItems.reduce(
//       (acc, item) => acc + item.discountedPrice * item.quantity,
//       0
//     );
//     const gstAmount = (subtotal * gstPercentage) / 100;
//     const totalPrice = subtotal + gstAmount;

//     // const gst = clickedItem.price * 0.18; // Example GST calculation
//     // const totalPrice = clickedItem.discountedPrice + gst;

//     navigate("/checkout", {
//       state: {
//         cart: cartItems, // Pass the whole cart array
//         gst: gstAmount,
//         totalPrice: totalPrice,
//       },
//     });
//   };

//   const [quantity, setQuantity] = useState(1);

//   return (
//     <div className="fixed top-0 bg-tertiary right-0 w-[90%] lg:w-1/3 h-full bg-white shadow-lg z-50 transition-transform duration-300 ease-in-out flex flex-col">
//       {/* Close Button */}
//       <div className="p-4 flex justify-end items-end">
//         <FontAwesomeIcon
//           icon={faTimes}
//           className="cursor-pointer text-primary1 text-xl"
//           onClick={toggleCartSlider}
//         />
//       </div>

//       <div className="px-7 flex-grow overflow-y-auto">
//         {cartItems.length > 0 ? (
//           <>
//             {/* Cart Heading and Clear All Button */}
//             <div className="flex items-center mb-4">
//               <h2 className="text-lg font-outfit text-hcolor">
//                 Cart ({cartItems.length})
//               </h2>
//               <button
//                 className="text-sm text-red-500 font-medium underline ml-auto"
//                 onClick={handleClearAll}
//               >
//                 Clear All
//               </button>
//             </div>

//             {/* Cart Items List */}
//             {cartItems.map((item) => {
//               // console.log("cart item----", item);
//               const price = item?.price || 0;
//               const discountedPrice =
//                 item?.discount_price || 0;
//               const image =
//                 item?.variant_main_image || "";
//                 const size =item?.size || "";
//                 const color = item?.color || "";
//                 const productName = item?.product_name || "";
//                 const qty = item?.quantity || 1;
//                 const variantId = item?.variant_id ||"0";

//               return (
//                 <div key={item.id} className="flex gap-4 mb-6 items-start ">
//                   <div className="relative w-[30%] lg:w-[20%]">
//                     <img
//                       src={IMAGE_URL + image}
//                       alt={image}
//                       className="w-full object-cover"
//                     />
//                     <button
//                       className="absolute -top-2 -right-2"
//                       onClick={() => handleRemove(variantId,size, color)}
//                     >
//                       <img src={icons.removeLikedItem} alt="remove item" />
//                     </button>
//                   </div>

//                   {/* Details */}
//                   <div className="flex flex-col flex-1 gap-2">
//                     <p className="font-outfit font-bold text-lg">
//                       {productName}
//                     </p>
//                     <p className="text-sm text-gray-500">
//                       Size: {size}
//                     </p>
//                     {/* Quantity Controls */}
//                     <div className="flex items-center gap-2">
//                       {/* Decrease Quantity */}
//                       <button
//                         onClick={() => handleQuantityChange(variantId,size, color, "decrement")}
//                         className="text-sm text-hcolor px-2 py-1 border rounded hover:bg-hcolor hover:text-white"
//                         disabled={item.quantity === 1}
//                       >
//                         <FontAwesomeIcon icon={faMinus} />
//                       </button>

//                       {/* Display Quantity */}
//                       <span className="text-sm">{qty}</span>

//                       {/* Increase Quantity */}
//                       <button
//                         onClick={() => handleQuantityChange(variantId,size, color, "increment")}
//                         className="text-sm text-hcolor px-2 py-1 border rounded hover:bg-hcolor hover:text-white"
//                       >
//                         <FontAwesomeIcon icon={faPlus} />
//                       </button>
//                     </div>
//                   </div>
//                   {/* Pricing and Stock */}
//                   <div className="text-right">
//                     <p className="text-lg font-bold text-primary">
//                       Rs.{discountedPrice}
//                     </p>
//                     <p className="text-sm text-gray-500 line-through">
//                       Rs.{price}
//                     </p>
//                     <p
//                       className={`text-sm ${
//                         item.inStock ? "text-green-500" : "text-red-500"
//                       }`}
//                     >
//                       {item.inStock ? "In Stock" : "Out of Stock"}
//                     </p>
//                   </div>
//                 </div>
//               );
//             })}
//           </>
//         ) : (
//           <div className="flex flex-col items-center mt-40 space-y-4">
//             <img
//               src={icons.emptyCart} // Replace with your image URL
//               alt="Rotating Animation"
//               className="w-32 transform transition-transform duration-500 hover:rotate-180" // Rotate on hover
//             />
//             <p className="text-center text-hcolor">Your cart is empty!</p>
//             <button
//               onClick={goToShop}
//               className="border-2 py-2 px-5 text-hcolor font-outfit font-medium hover:bg-hcolor hover:text-primary2 bg-white transition-all duration-500"
//             >
//               Shop Now
//             </button>
//           </div>
//         )}
//       </div>

//       {/* Subtotal Section */}
//       {cartItems.length > 0 && (
//         <div className="px-7 py-4 border-t bg-primary2">
//           <div className="flex justify-between text-lg font-medium">
//             <span>Subtotal</span>
//             <span>Rs.{subTotal}</span>
//           </div>
//           <p className="text-sm text-gray-500">
//             Tax Included, shipping calculated at checkout.
//           </p>
//           <button
//             className="mt-10 w-full py-3 bg-hcolor text-tertiary font-medium hover:bg-primary-dark transition-all duration-300"
//             onClick={handleCheckout}
//           >
//             Checkout
//           </button>
//         </div>
//       )}
//     </div>
//   );
// };

// export default CartSlider;
